import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/auth';

export function DreamDetailPage() {
  const { dreamId } = useParams({ from: '/dreams/$dreamId' });
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isProcessing = (status?: string) =>
    status === 'start' || status === 'run' || status === 'img';

  const { data: dream, isLoading } = useQuery({
    queryKey: ['dream', dreamId],
    queryFn: () => api.dreams.get(dreamId),
    enabled: !!user,
    refetchInterval: (query) =>
      isProcessing(query.state.data?.status) ? 3000 : false,
  });

  const analyzeMutation = useMutation({
    mutationFn: () => api.dreams.analyze(dreamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dream', dreamId] });
    },
  });

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <Link to="/login" className="btn-primary">Login to view</Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="card animate-pulse">
          <div className="h-8 bg-dream-700 rounded w-1/2 mb-4" />
          <div className="h-64 bg-dream-700 rounded" />
        </div>
      </div>
    );
  }

  if (!dream) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-white mb-4">Dream not found</h1>
        <Link to="/dreams" className="btn-primary">Back to Dreams</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <Link to="/dreams" className="text-dream-400 hover:text-white text-sm mb-4 block">
        &larr; Back to dreams
      </Link>

      {/* Dream Image */}
      {dream.images && dream.images.length > 0 && (
        <div className="mb-8 rounded-xl overflow-hidden">
          <img
            src={dream.images[0].url}
            alt="Dream visualization"
            className="w-full h-auto"
          />
        </div>
      )}

      {/* Title and Reading */}
      {dream.reading && (
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold text-white mb-4">
            {dream.reading.title}
          </h1>
          <p className="text-xl text-dream-200">{dream.reading.shortSummary}</p>
        </div>
      )}

      {/* Raw Dream */}
      <div className="card mb-8">
        <h2 className="text-lg font-semibold text-white mb-3">The Dream</h2>
        <p className="text-dream-200 whitespace-pre-wrap">{dream.rawContent}</p>
        {dream.howYouFeel && (
          <div className="mt-4 pt-4 border-t border-dream-800">
            <span className="text-dream-400 text-sm">How I felt:</span>
            <p className="text-dream-200">{dream.howYouFeel}</p>
          </div>
        )}
      </div>

      {/* Interpretation */}
      {dream.reading && (
        <>
          <div className="card mb-8">
            <h2 className="text-lg font-semibold text-white mb-3">Interpretation</h2>
            <p className="text-dream-200 whitespace-pre-wrap">{dream.reading.interpretation}</p>
          </div>

          {/* Symbols and Archetypes */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {dream.reading.symbols && dream.reading.symbols.length > 0 && (
              <div className="card">
                <h3 className="text-lg font-semibold text-white mb-3">Symbols</h3>
                <div className="flex flex-wrap gap-2">
                  {dream.reading.symbols.map((symbol: string) => (
                    <span key={symbol} className="px-3 py-1 bg-dream-800 text-dream-200 rounded-full text-sm">
                      {symbol}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {dream.reading.archetypes && dream.reading.archetypes.length > 0 && (
              <div className="card">
                <h3 className="text-lg font-semibold text-white mb-3">Archetypes</h3>
                <div className="flex flex-wrap gap-2">
                  {dream.reading.archetypes.map((archetype: string) => (
                    <span key={archetype} className="px-3 py-1 bg-purple-900/50 text-purple-300 rounded-full text-sm">
                      {archetype}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Actions */}
      <div className="flex gap-4">
        <Link
          to="/subconscious"
          search={{ startWith: dreamId }}
          className="btn-primary"
        >
          Explore in Subconscious
        </Link>
        {dream.status === 'new' && (
          <button
            className="btn-secondary"
            disabled={analyzeMutation.isPending}
            onClick={() => analyzeMutation.mutate()}
          >
            {analyzeMutation.isPending ? 'Queuing...' : 'Analyze Dream'}
          </button>
        )}
        {isProcessing(dream.status) && (
          <button className="btn-secondary" disabled>
            Processing...
          </button>
        )}
        {dream.status === 'complete' && !dream.reading && (
          <button
            className="btn-secondary"
            disabled={analyzeMutation.isPending}
            onClick={() => analyzeMutation.mutate()}
          >
            {analyzeMutation.isPending ? 'Queuing...' : 'Re-analyze Dream'}
          </button>
        )}
      </div>
    </div>
  );
}
